import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  MenuItem,
  Grid,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import Q8Button from "../components/Q8Button";
import Q8SnackBar from "../components/Q8SnackBar";
import {
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext.js";
import OpexService from "../service/opexService.js";
import Q8NoData from "../components/Q8Nodata.js";

const OpexChangeHistory = ({onlyAllocation, title, href, secondHref}) => {
  // State
  const [changeHistory, setChangeHistory] = useState([]);
  const [expandedRecords, setExpandedRecords] = useState({});
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "default",
  });
  const [loading, _] = useState(true);
  const [filters, setFilters] = useState({
    status: "all",
    dateFrom: "",
    dateTo: "",
    identifier: "", // Cambiato da contract a identifier
  });

  const { user, bestRole } = useContext(UserContext);

  // Helper functions
  const showNotification = (message, type = "default") => {
    setSnackbar({ open: true, message, type });
  };

  const toggleExpandRecord = (id) => {
    setExpandedRecords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const loadChangeHistory = async () => {
    try {
      // Converti filters.identifier al vecchio filtro contract per API
      const apiFilters = {
        ...filters,
        contract: filters.identifier, // Per retrocompatibilità con l'API
      };
      delete apiFilters.identifier;

      const response = await OpexService.getRecordChangeHistory(apiFilters, onlyAllocation);

      // Parse JSON strings to objects
      const processedChanges = response.map((change) => {
        let originalRecord = {};
        let modifiedRecord = {};

        try {
          originalRecord = JSON.parse(change.originalRecordJson || "{}");
        } catch (e) {
          console.error("Error parsing originalRecordJson:", e);
        }

        try {
          modifiedRecord = JSON.parse(change.modifiedRecordJson || "{}");
        } catch (e) {
          console.error("Error parsing modifiedRecordJson:", e);
        }

        return {
          ...change,
          originalRecord,
          modifiedRecord,
        };
      });

      setChangeHistory(processedChanges);
    } catch (error) {
      console.error("Errore caricando storico modifiche:", error);
      showNotification("Errore caricando storico modifiche", "error");
    }
  };

  // Load data when component mounts or filters change
  useEffect(() => {
    loadChangeHistory();
  }, [filters]);

  const handleFilterChange = (key) => (e) => {
    setFilters({
      ...filters,
      [key]: e.target.value,
    });
  };

  // Function to detect and highlight differences between original and modified records
  const isDifferent = (key, originalRecord, modifiedRecord) => {
    if (!originalRecord || !modifiedRecord) return false;

    // For numbers, convert to string for comparison display purposes
    const originalValue =
      typeof originalRecord[key] === "number"
        ? originalRecord[key].toString()
        : originalRecord[key];

    const modifiedValue =
      typeof modifiedRecord[key] === "number"
        ? modifiedRecord[key].toString()
        : modifiedRecord[key];

    return originalValue !== modifiedValue;
  };

  // Function to format values for display
  const formatValue = (key, value) => {
    if (value === null || value === undefined) return "-";

    // Format monetary values
    if (["euro", "euro_SAP"].includes(key) && typeof value === "number") {
      return new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    }

    // Format dates
    if (
      ["dataDocumento", "dataRegistrazione"].includes(key) &&
      value instanceof Date
    ) {
      return value.toLocaleDateString();
    }

    return value.toString();
  };

  // Get the keys to display from the record
  const getKeysToDisplay = (record) => {
    if (!record) return [];

    // Define keys to display in the order you want
    const orderedKeys = [
      "contract",
      "flusso",
      "centroDiCosto",
      "descCdc",
      "voceDiCosto",
      "descConto",
      "creatoreDocumentoCO",
      "contoDiContropartita",
      "desConto",
      "coDocumentNumber",
      "posizioneDocumentoCO",
      "documentoDiRiferimento",
      "posizioneDocumentoDiRiferimento",
      "periodo",
      "esercizio",
      "contabilitSecond",
      "fornitore",
      "descFornitore",
      "cliente",
      "descCliente",
      "oda",
      "posizioneOda",
      "descOda",
      "buyer",
      "collaudatoDa",
      "dataDocumento",
      "dataRegistrazione",
      "testoDocumentoCO",
      "descrizioneDocCO",
      "euro",
      "quantita",
      "corporate",
      "sap_OU",
      "raggruppamento1",
      "raggruppamento2",
      "raggruppamento3",
      "responsabile",
    ];

    // Filter out keys that don't exist in the record
    return orderedKeys.filter((key) => record.hasOwnProperty(key));
  };

  // Function to get a more user-friendly field name
  const getFieldDisplayName = (key) => {
    const fieldNameMap = {
      contract: "Contratto",
      flusso: "Flusso",
      centroDiCosto: "CDC",
      descCdc: "Desc CDC",
      voceDiCosto: "Conto",
      descConto: "Desc Conto",
      creatoreDocumentoCO: "Creatore Documento CO",
      contoDiContropartita: "Conto di Contropartita",
      desConto: "Des Conto Contropartita",
      coDocumentNumber: "CO Numero documento",
      posizioneDocumentoCO: "Posizione Documento CO",
      documentoDiRiferimento: "Documento di Riferimento",
      posizioneDocumentoDiRiferimento: "Posizione Doc Riferimento",
      periodo: "Mese Fiscale",
      esercizio: "Esercizio",
      contabilitSecond: "Contabilit Second",
      fornitore: "Fornitore",
      descFornitore: "Desc Fornitore",
      cliente: "Cliente",
      descCliente: "Desc Cliente",
      oda: "Oda",
      posizioneOda: "Posizione Oda",
      descOda: "Desc Oda",
      buyer: "Acquirente",
      collaudatoDa: "Collaudato Da",
      dataDocumento: "Data Documento",
      dataRegistrazione: "Data Registrazione",
      testoDocumentoCO: "Testo Documento CO",
      descrizioneDocCO: "Descrizione Doc CO",
      euro: "Euro",
      quantita: "Quantità",
      corporate: "Local/Corporate",
      sap_OU: "SAP OU",
      raggruppamento1: "Liv 1",
      raggruppamento2: "Liv 2",
      raggruppamento3: "Liv 3",
      responsabile: "Responsabile",
    };

    return fieldNameMap[key] || key;
  };

  // Get status chip color
  const getStatusChipColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  // Get status chip text
  const getStatusChipText = (status) => {
    switch (status) {
      case "approved":
        return "Approvato";
      case "rejected":
        return "Rifiutato";
      case "pending":
        return "In attesa";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  // Ottiene identificativo OPEX (Contract o OpexOriginalId)
  const getOpexIdentifier = (change) => {
    return change.contract || change.opexOriginalId;
  };

  return (
    <Box
      sx={{ height: "100vh", display: "flex", flexDirection: "column", p: 2 }}
    >
      <Q8Breadcrumbs
        links={[
          { label: title, href: href },
          { label: "Storico modifiche", href: secondHref },
        ]}
        variant="primary"
      />

      <Box sx={{ mt: 3, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            {title} Storico modifiche
          </Typography>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Q8Button
              variant="secondary"
              label={filtersVisible ? "Nascondi Filtri" : "Mostra Filtri"}
              onClick={() => setFiltersVisible(!filtersVisible)}
              icon={<FilterIcon />}
              size="small"
            />
            <Q8Button
              variant="secondary"
              label="Aggiorna Dati"
              onClick={loadChangeHistory}
              icon={<RefreshIcon />}
              size="small"
            />
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mt: 1 }}>
          Vista dello storico modifiche per gli {title} records.
        </Typography>

        {filtersVisible && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label="Stato"
                  value={filters.status}
                  onChange={handleFilterChange("status")}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="approved">Approvato</MenuItem>
                  <MenuItem value="rejected">Rifiutato</MenuItem>
                  <MenuItem value="pending">In attesa</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="Data di inizio"
                  value={filters.dateFrom}
                  onChange={handleFilterChange("dateFrom")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  type="date"
                  label="Data di fine"
                  value={filters.dateTo}
                  onChange={handleFilterChange("dateTo")}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Indentificativo (Contratto/ID Originale)"
                  value={filters.identifier}
                  onChange={handleFilterChange("identifier")}
                  size="small"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
                <b>ID</b>
              </TableCell>
              <TableCell>
                <b>Identificativo</b>
              </TableCell>
              <TableCell>
                <b>Modificato da</b>
              </TableCell>
              <TableCell>
                <b>Modificato il</b>
              </TableCell>
              <TableCell>
                <b>Stato</b>
              </TableCell>
              <TableCell>
                <b>Gestito da</b>
              </TableCell>
              <TableCell>
                <b>Gestito il</b>
              </TableCell>
              <TableCell>
                <b>Note</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {changeHistory.map((change) => (
              <React.Fragment key={change.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleExpandRecord(change.id)}
                    >
                      {expandedRecords[change.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>{change.id}</TableCell>
                  <TableCell>{getOpexIdentifier(change)}</TableCell>
                  <TableCell>{change.modifiedBy}</TableCell>
                  <TableCell>{formatDate(change.modifiedAt)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusChipText(change.status)}
                      color={getStatusChipColor(change.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{change.handledBy || "-"}</TableCell>
                  <TableCell>{formatDate(change.handledAt)}</TableCell>
                  <TableCell>{change.notes || "-"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={9}
                  >
                    <Collapse
                      in={expandedRecords[change.id]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box sx={{ m: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Dettagli modifica per {title} {getOpexIdentifier(change)}
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <b>Campo</b>
                              </TableCell>
                              <TableCell>
                                <b>Valore originale</b>
                              </TableCell>
                              <TableCell>
                                <b>Valore modificato</b>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {getKeysToDisplay(change.originalRecord).map(
                              (key) => {
                                const isDiff = isDifferent(
                                  key,
                                  change.originalRecord,
                                  change.modifiedRecord
                                );
                                return (
                                  <TableRow
                                    key={key}
                                    sx={{
                                      backgroundColor: isDiff
                                        ? "rgba(255, 243, 224, 0.5)"
                                        : "inherit",
                                    }}
                                  >
                                    <TableCell>
                                      {getFieldDisplayName(key)}
                                    </TableCell>
                                    <TableCell>
                                      {formatValue(
                                        key,
                                        change.originalRecord[key]
                                      )}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        fontWeight: isDiff ? "bold" : "normal",
                                        color: isDiff
                                          ? "primary.main"
                                          : "inherit",
                                      }}
                                    >
                                      {formatValue(
                                        key,
                                        change.modifiedRecord[key]
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              }
                            )}
                            {getKeysToDisplay(change.originalRecord).length ===
                              0 && (
                              <TableRow>
                                <TableCell colSpan={3} align="center">
                                  No differences found or incomplete data
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        {changeHistory.length === 0 && <Q8NoData />}
      </TableContainer>

      {/* Snackbar for notifications */}
      <Q8SnackBar
        open={snackbar.open}
        type={snackbar.type}
        title="Notification"
        message={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default OpexChangeHistory;
